import * as React from 'react';
import { styled } from '@linaria/react';

import useTickSfx from '@/hooks/use-tick-sfx';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Space from '@/components/Space';

import {
  POSITION_RADIUS,
  Svg,
  Label,
  PositionDot,
  CodeSnippet,
  Indicators,
  Row,
} from './BasicShapes.shared';
import GuideLine from './GuideLine';
import SizeLine from './SizeLine';

const SIZE = 280;

function BasicCircleDemo({
  variant = 'default',
}: {
  variant?: 'default' | 'simplified';
}) {
  const id = React.useId().replace(/:/g, '');
  const maskId = `${id}-circle-mask`;

  const playTick = useTickSfx();

  const maxRadius = Math.round(
    SIZE * (variant === 'default' ? 0.5 : 0.25)
  );

  const controls = [
    <DemoSliderControl
      highlightControlWhenActive
      key="r"
      id="r"
      label="r"
      secondaryLabel="(radius)"
      min={0}
      max={maxRadius}
      wrapperStyle={{
        gridArea: 'r',
      }}
      onChange={() => {
        playTick();
      }}
    />,
  ];

  if (variant === 'default') {
    controls.unshift(
      <DemoSliderControl
        highlightControlWhenActive
        key="cx"
        id="cx"
        label="cx"
        secondaryLabel="(center x)"
        min={0}
        max={SIZE}
        step={10}
        wrapperStyle={{
          gridArea: 'cx',
        }}
        onChange={() => {
          playTick();
        }}
      />,
      <DemoSliderControl
        highlightControlWhenActive
        key="cy"
        id="cy"
        label="cy"
        secondaryLabel="(center y)"
        min={0}
        max={SIZE}
        step={10}
        wrapperStyle={{
          gridArea: 'cy',
        }}
        onChange={() => {
          playTick();
        }}
      />
    );
  }

  return (
    <DemoWrapper data-variant={variant}>
      <Demo
        id="basic-circle-demo"
        initialValues={{
          cx: Math.round(SIZE / 2),
          cy: Math.round(SIZE / 2),
          r:
            variant === 'default'
              ? Math.round(SIZE / 4)
              : Math.round(SIZE * 0.125),
        }}
        outputWrapper={OutputWrapper}
        CustomGridComponent={ControlsGrid}
        controls={controls}
      >
        {({ cx, cy, r }, activeControlId) => {
          // Calculate the endpoint for radius line (45 degrees down-right). Also, subtract 5px from radius to avoid overlapping with circle stroke
          const adjustedRadius = Math.max(r - 6, 0);
          const angle = Math.PI / 4;
          const startX = cx + POSITION_RADIUS;
          const startY = cy + POSITION_RADIUS;
          const endX = cx + adjustedRadius * Math.cos(angle);
          const endY = cy + adjustedRadius * Math.sin(angle);

          const midX =
            startX + adjustedRadius * Math.cos(angle) * 0.4;
          const midY =
            startY + adjustedRadius * Math.sin(angle) * 0.4;

          const code = `\
<svg width="${SIZE}" height="${SIZE}">
  <circle
    cx="${cx}"
    cy="${cy}"
    r="${r}"
    fill="none"
    stroke="oklch(0.9 0.3 164)"
    stroke-width="5"
  />
</svg>\
`;

          return (
            <StyledRow data-variant={variant}>
              {variant === 'default' && (
                <StyledCodeSnippet code={code} />
              )}

              <SvgWrapper>
                <Svg
                  size={SIZE}
                  defs={
                    <mask id={maskId}>
                      <rect width={SIZE} height={SIZE} fill="black" />
                      <circle
                        cx={cx}
                        cy={cy}
                        r={Math.max(r - 5, 0)}
                        fill="white"
                      />
                    </mask>
                  }
                >
                  <PositionDot
                    cx={cx}
                    cy={cy}
                    mask={`url(#${maskId})`}
                  />

                  {/* MAIN SHAPE: */}
                  <circle
                    cx={cx}
                    cy={cy}
                    r={r}
                    fill="transparent"
                    stroke="var(--wham-complementary-500)"
                    strokeWidth={5}
                  />
                  <Indicators
                    style={{
                      opacity: r === 0 ? 0 : 1,
                    }}
                  >
                    {/* Guidelines — visible white lines */}
                    {/* cx line */}
                    <GuideLine
                      x1={cx - POSITION_RADIUS - 2}
                      y1={cy}
                      x2={0}
                      y2={cy}
                      isActive={activeControlId === 'cx'}
                    />
                    {/* cy line */}
                    <GuideLine
                      x1={cx}
                      y1={cy - POSITION_RADIUS - 2}
                      x2={cx}
                      y2={0}
                      isActive={activeControlId === 'cy'}
                    />
                    {/* radius line */}
                    <SizeLine
                      x1={startX}
                      y1={startY}
                      x2={endX}
                      y2={endY}
                      isActive={activeControlId === 'r'}
                      isHidden={r <= 13}
                      mask={`url(#${maskId})`}
                    />

                    <Label
                      x={cx / 2}
                      y={cy + 20}
                      textAnchor="middle"
                      dominantBaseline="bottom"
                      data-is-hidden={cx < 30}
                      data-is-active={activeControlId === 'cx'}
                    >
                      cx
                    </Label>
                    <Label
                      x={cx + 30}
                      y={cy / 2}
                      textAnchor="end"
                      dominantBaseline="middle"
                      data-is-hidden={cy < 20}
                      data-is-active={activeControlId === 'cy'}
                    >
                      cy
                    </Label>
                    <Label
                      x={midX + 8}
                      y={midY - 8}
                      textAnchor="start"
                      dominantBaseline="middle"
                      mask={`url(#${maskId})`}
                      data-is-active={activeControlId === 'r'}
                    >
                      r
                    </Label>
                  </Indicators>
                </Svg>
              </SvgWrapper>
            </StyledRow>
          );
        }}
      </Demo>
    </DemoWrapper>
  );
}

const BREAKPOINT = '37rem';

const DemoWrapper = styled.div``;

const OutputWrapper = styled(Space)`
  container-type: inline-size;
  background: var(--color-background);
`;

const ControlsGrid = styled.div`
  display: grid;
  gap: 16px 32px;
  grid-template-areas:
    'cx'
    'cy'
    'r';

  @media (min-width: 30rem) {
    grid-template-areas:
      'cx cy'
      'r  r';
  }
`;

const StyledRow = styled(Row)`
  @container (max-width: ${BREAKPOINT}) {
    flex-direction: column;
    align-items: center;
  }
`;

const StyledCodeSnippet = styled(CodeSnippet)`
  @container (max-width: ${BREAKPOINT}) {
    width: 100%;
  }
`;

const SvgWrapper = styled.div`
  display: flex;

  @container (max-width: ${BREAKPOINT}) {
    position: relative;
  }
`;

export default BasicCircleDemo;
